import React, { useMemo, useState } from "react";
import { createBranch, createDepartment } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { useIndustry, useIndustryLabels } from "../../context/IndustryContext";
import { useOrganizationStructure } from "../../hooks/useOrganizationStructure";
import {
  ADMINISTRATION_KEY,
  getBranchDisplayOptions,
  suggestBranchCode,
} from "../../utils/dataMappers";
import {
  buildSectionGroups,
  departmentUsesBranches,
  filterOpsBranchEmployees,
  isOperationsDepartment,
  isOpsHeadquartersEmployee,
} from "../../utils/orgHelpers";
import { TENANT_INDUSTRY } from "../../utils/industryLabels";
import EmployeeHistoryView from "../common/EmployeeHistoryView";
import "./BranchesDepartments.css";
import "../common/EmployeeHistoryView.css";

function suggestDepartmentCode(name) {
  const latin = name
    .trim()
    .replace(/[^\w\s]/g, "")
    .split(/\s+/)
    .map((w) => w.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 6);
  return latin.length >= 2 ? latin : `D${Date.now().toString().slice(-4)}`;
}

function managedBranchNames(emp) {
  return (emp?.managedBranches || []).map((branch) => branch?.name).filter(Boolean);
}

function managesBranch(emp, branchId) {
  if (!emp || branchId == null) return false;
  const id = String(branchId);
  if ((emp.managedBranchIds || []).some((item) => String(item) === id)) {
    return true;
  }
  return (emp.managedBranches || []).some((branch) => String(branch?.id) === id);
}

function EmployeeTable({ employees, searchTerm, onSelectHistory }) {
  const filteredEmployees = employees.filter(
    (emp) =>
      emp.name.includes(searchTerm) ||
      emp.code.includes(searchTerm) ||
      emp.jobTitle.includes(searchTerm),
  );

  function getStatusBadge(status) {
    if (status === "active") {
      return <span className="bd-status active">نشط</span>;
    }
    if (status === "on-leave") {
      return <span className="bd-status on-leave">في إجازة</span>;
    }
    return <span className="bd-status on-leave">غير نشط</span>;
  }

  return (
    <div className="bd-table-card">
      <table className="bd-table">
        <thead>
          <tr>
            <th>الموظف</th>
            <th>المسمى الوظيفي</th>
            <th>تاريخ الانضمام</th>
            <th>الحالة</th>
            <th>إجراءات</th>
          </tr>
        </thead>
        <tbody>
          {filteredEmployees.map((emp) => (
            <tr
              key={emp.id}
              className="clickable"
              onClick={() => onSelectHistory(emp)}
            >
              <td>
                <div className="bd-employee-info">
                  <div className="bd-avatar">{emp.name.charAt(0)}</div>
                  <div>
                    <div className="bd-employee-name">{emp.name}</div>
                    <div className="bd-employee-code">{emp.code}</div>
                  </div>
                </div>
              </td>
              <td>{emp.jobTitle}</td>
              <td>{emp.joinDate}</td>
              <td>{getStatusBadge(emp.status)}</td>
              <td>
                <button
                  className="bd-action-btn history"
                  title="عرض السجل"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectHistory(emp);
                  }}
                >
                  📋 السجل
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {filteredEmployees.length === 0 && (
        <p className="bd-empty">لا يوجد موظفين مطابقين للبحث</p>
      )}
    </div>
  );
}

function BranchesDepartments({ userRole = "department-manager" }) {
  const { user } = useAuth();
  const labels = useIndustryLabels();
  const { industry } = useIndustry();
  const { selected: orgStructure } = useOrganizationStructure();
  const {
    branches,
    departments,
    employeesByBranch,
    getManagedEmployees,
    getAllEmployees,
    refreshData,
  } = useData();

  const viewerDepartmentId = user?.departmentId;
  const isOrgWideViewer =
    userRole === "gm" || userRole === "admin" || userRole === "owner";
  const canManageStructure = userRole === "gm" || userRole === "admin";
  const isFactory = industry === TENANT_INDUSTRY.FACTORY;
  const showRegionalManagers = (orgStructure?.ranks || []).includes(
    "regional-manager",
  );
  const isOpsDm =
    userRole === "department-manager" &&
    isOperationsDepartment(viewerDepartmentId);
  const isOpsViewer = isOrgWideViewer || isOpsDm;
  const isSectionViewer =
    userRole === "department-manager" &&
    viewerDepartmentId &&
    !isOperationsDepartment(viewerDepartmentId);

  const managedEmployees = useMemo(
    () =>
      isOrgWideViewer
        ? getAllEmployees()
        : getManagedEmployees(user?.employeeId, userRole),
    [getAllEmployees, getManagedEmployees, isOrgWideViewer, user?.employeeId, userRole],
  );

  const branchOptions = useMemo(
    () => getBranchDisplayOptions(branches),
    [branches],
  );

  const opsBranchCards = useMemo(() => {
    return branchOptions
      .filter((branch) => branch.id !== ADMINISTRATION_KEY)
      .map((branch) => {
        const employees = filterOpsBranchEmployees(
          employeesByBranch[branch.id] || [],
        ).filter((emp) =>
          isOrgWideViewer ? true : managedEmployees.some((m) => m.id === emp.id),
        );

        return {
          ...branch,
          employees,
          total: employees.length,
          active: employees.filter((e) => e.status === "active").length,
        };
      });
  }, [branchOptions, employeesByBranch, isOrgWideViewer, managedEmployees]);

  const opsHqEmployees = useMemo(() => {
    const pool = isOrgWideViewer
      ? getAllEmployees().filter(isOpsHeadquartersEmployee)
      : managedEmployees.filter(isOpsHeadquartersEmployee);
    return pool;
  }, [getAllEmployees, isOrgWideViewer, managedEmployees]);

  const opsHqSections = useMemo(() => {
    const sections = buildSectionGroups(opsHqEmployees);
    // The catch-all "الإدارة" bucket lists the operations manager himself
    // plus HQ staff who are not in a real section (warehouse, V.M.).
    // Hide that card from the operations manager's own structure view.
    if (!isOpsDm) return sections;
    return sections.filter(
      (section) =>
        section.id !== "department" &&
        section.id !== "administration" &&
        section.name !== "الإدارة",
    );
  }, [isOpsDm, opsHqEmployees]);

  const opsDeptName =
    departments.find((d) => isOperationsDepartment(d.id))?.name ||
    "إدارة التشغيل";

  const sectionCards = useMemo(() => {
    const employeesForDept = (dept) =>
      managedEmployees.filter(
        (emp) =>
          emp.departmentId === dept.id ||
          (dept.backendId && emp.departmentBackendId === dept.backendId),
      );

    if (isOrgWideViewer) {
      // Show every active department in org structure (including ones created
      // from the employee form). OPS keeps its separate branches/HQ sections.
      return departments
        .filter((dept) => dept.isActive !== false)
        .map((dept) => {
          const employees = employeesForDept(dept);
          return {
            id: dept.id,
            name: dept.name,
            code: dept.code,
            isOps: departmentUsesBranches(dept.id),
            isActive: dept.isActive !== false,
            employees,
            total: employees.length,
            active: employees.filter((e) => e.status === "active").length,
            sections: buildSectionGroups(employees),
          };
        });
    }

    const employees = managedEmployees.filter(
      (emp) =>
        emp.departmentId === viewerDepartmentId ||
        emp.departmentBackendId ===
          departments.find((d) => d.id === viewerDepartmentId)?.backendId,
    );
    const deptName =
      departments.find((d) => d.id === viewerDepartmentId)?.name || "الإدارة";

    return [
      {
        id: viewerDepartmentId,
        name: deptName,
        employees,
        total: employees.length,
        active: employees.filter((e) => e.status === "active").length,
        sections: buildSectionGroups(employees),
      },
    ];
  }, [departments, isOrgWideViewer, managedEmployees, viewerDepartmentId]);

  const regionalManagers = useMemo(() => {
    const pool = isOrgWideViewer ? getAllEmployees() : managedEmployees;
    return pool
      .filter(
        (emp) =>
          emp.memberLevel === "regional-manager" &&
          emp.status !== "terminated" &&
          isOperationsDepartment(emp.departmentId),
      )
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "ar"));
  }, [getAllEmployees, isOrgWideViewer, managedEmployees]);

  const [viewMode, setViewMode] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [openedFromRegionalId, setOpenedFromRegionalId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [historyEmployee, setHistoryEmployee] = useState(null);

  const [showBranchForm, setShowBranchForm] = useState(false);
  const [newBranchName, setNewBranchName] = useState("");
  const [newBranchCode, setNewBranchCode] = useState("");
  const [creatingBranch, setCreatingBranch] = useState(false);
  const [branchError, setBranchError] = useState("");

  const [showDeptForm, setShowDeptForm] = useState(false);
  const [newDeptName, setNewDeptName] = useState("");
  const [newDeptCode, setNewDeptCode] = useState("");
  const [creatingDept, setCreatingDept] = useState(false);
  const [deptError, setDeptError] = useState("");

  function resetSelection() {
    setViewMode(null);
    setSelectedId(null);
    setSelectedSectionId(null);
    setOpenedFromRegionalId(null);
    setSearchTerm("");
  }

  function openBranch(branchId, fromRegionalId = null) {
    setViewMode("branch");
    setSelectedId(branchId);
    setSelectedSectionId(null);
    setOpenedFromRegionalId(fromRegionalId);
    setSearchTerm("");
  }

  async function handleCreateBranch() {
    const name = newBranchName.trim();
    const code = newBranchCode.trim().toUpperCase() || suggestBranchCode(name);
    setBranchError("");

    if (!name) {
      setBranchError(`من فضلك أدخل اسم ${labels.site}`);
      return;
    }
    if (!/^[A-Z0-9_]{2,10}$/.test(code)) {
      setBranchError(
        `كود ${labels.site}: 2-10 أحرف/أرقام بالإنجليزية (مثل MAADI)`,
      );
      return;
    }

    setCreatingBranch(true);
    try {
      await createBranch({ name, code });
      await refreshData();
      setNewBranchName("");
      setNewBranchCode("");
      setShowBranchForm(false);
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.error;
      setBranchError(
        Array.isArray(message)
          ? message.join("\n")
          : message || `تعذر إنشاء ${labels.site}`,
      );
    } finally {
      setCreatingBranch(false);
    }
  }

  async function handleCreateDepartment() {
    const name = newDeptName.trim();
    const code = newDeptCode.trim().toUpperCase() || suggestDepartmentCode(name);
    setDeptError("");

    if (!name) {
      setDeptError("من فضلك أدخل اسم الإدارة");
      return;
    }
    if (!/^[A-Z0-9_]{2,10}$/.test(code)) {
      setDeptError("كود الإدارة: 2-10 أحرف/أرقام بالإنجليزية (مثل SALES)");
      return;
    }

    setCreatingDept(true);
    try {
      await createDepartment({ name, code });
      await refreshData();
      setNewDeptName("");
      setNewDeptCode("");
      setShowDeptForm(false);
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.error;
      setDeptError(
        Array.isArray(message)
          ? message.join("\n")
          : message || "تعذر إنشاء الإدارة",
      );
    } finally {
      setCreatingDept(false);
    }
  }

  if (historyEmployee) {
    return (
      <EmployeeHistoryView
        employee={historyEmployee}
        userRole={userRole}
        onBack={() => setHistoryEmployee(null)}
      />
    );
  }

  if (viewMode === "regional" && selectedId) {
    const manager = regionalManagers.find((emp) => emp.id === selectedId);
    const branchNames = managedBranchNames(manager);
    const coveredBranches = opsBranchCards.filter((branch) =>
      managesBranch(manager, branch.id),
    );

    return (
      <div className="bd-container" dir="rtl">
        <div className="bd-header">
          <button className="bd-back-btn" onClick={resetSelection}>
            ← رجوع
          </button>
          <div>
            <h2 className="bd-title">{manager?.name || "مدير منطقة"}</h2>
            <p className="bd-subtitle">
              {manager?.jobTitle || "مدير منطقة"}
              {branchNames.length
                ? ` — ${branchNames.join("، ")}`
                : " — بدون فروع مرتبطة"}
            </p>
          </div>
        </div>
        <EmployeeTable
          employees={manager ? [manager] : []}
          searchTerm=""
          onSelectHistory={setHistoryEmployee}
        />
        {coveredBranches.length > 0 && (
          <>
            <h3 className="bd-section-label">الفروع التابعة</h3>
            <div className="bd-branches-grid">
              {coveredBranches.map((branch) => (
                <button
                  key={branch.id}
                  className="bd-branch-card"
                  onClick={() => openBranch(branch.id, manager.id)}
                >
                  <div className="bd-branch-icon">🏢</div>
                  <div className="bd-branch-name">{branch.name}</div>
                  <div className="bd-branch-stats">
                    {branch.total} موظف | {branch.active} نشط
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  if (viewMode === "branch" && selectedId) {
    const branch = opsBranchCards.find((b) => b.id === selectedId);
    const employees = branch?.employees || [];
    const areaManager = regionalManagers.find((emp) =>
      managesBranch(emp, selectedId),
    );

    return (
      <div className="bd-container" dir="rtl">
        <div className="bd-header">
          <button
            className="bd-back-btn"
            onClick={() => {
              if (openedFromRegionalId) {
                setViewMode("regional");
                setSelectedId(openedFromRegionalId);
                setOpenedFromRegionalId(null);
                setSearchTerm("");
                return;
              }
              resetSelection();
            }}
          >
            {openedFromRegionalId ? "← رجوع لمدير المنطقة" : "← رجوع للفروع"}
          </button>
          <div>
            <h2 className="bd-title">{branch?.name}</h2>
            <p className="bd-subtitle">
              {employees.length} موظف تشغيل |{" "}
              {employees.filter((e) => e.status === "active").length} نشط
              {areaManager ? ` | مدير المنطقة: ${areaManager.name}` : ""}
            </p>
          </div>
        </div>
        <div className="bd-search">
          <input
            type="text"
            placeholder="بحث بالاسم، الكود، أو المسمى الوظيفي..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bd-search-input"
          />
        </div>
        <EmployeeTable
          employees={employees}
          searchTerm={searchTerm}
          onSelectHistory={setHistoryEmployee}
        />
      </div>
    );
  }

  if (viewMode === "ops-hq" && selectedSectionId) {
    const section = opsHqSections.find((s) => s.id === selectedSectionId);

    return (
      <div className="bd-container" dir="rtl">
        <div className="bd-header">
          <button
            className="bd-back-btn"
            onClick={() => {
              setSelectedSectionId(null);
              setViewMode(null);
              setSearchTerm("");
            }}
          >
            ← رجوع للأقسام
          </button>
          <div>
            <h2 className="bd-title">{section?.name}</h2>
            <p className="bd-subtitle">قسم تابع لـ {opsDeptName}</p>
          </div>
        </div>
        <div className="bd-search">
          <input
            type="text"
            placeholder="بحث بالاسم، الكود، أو المسمى الوظيفي..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bd-search-input"
          />
        </div>
        <EmployeeTable
          employees={section?.employees || []}
          searchTerm={searchTerm}
          onSelectHistory={setHistoryEmployee}
        />
      </div>
    );
  }

  if (viewMode === "department" && selectedId) {
    const dept = sectionCards.find((d) => d.id === selectedId);
    const sections = dept?.sections || [];

    if (!selectedSectionId) {
      return (
        <div className="bd-container" dir="rtl">
          <div className="bd-header">
            <button className="bd-back-btn" onClick={resetSelection}>
              ← رجوع
            </button>
            <div>
              <h2 className="bd-title">أقسام {dept?.name}</h2>
              <p className="bd-subtitle">
                {dept?.total} موظف — بدون فروع ميدانية
              </p>
            </div>
          </div>
          <div className="bd-branches-grid">
            {sections.length === 0 ? (
              <p className="bd-empty">لا يوجد موظفون في هذه الإدارة</p>
            ) : (
              sections.map((section) => (
                <button
                  key={section.id}
                  className="bd-branch-card"
                  onClick={() => setSelectedSectionId(section.id)}
                >
                  <div className="bd-branch-icon">📁</div>
                  <div className="bd-branch-name">{section.name}</div>
                  <div className="bd-branch-stats">
                    {section.employees.length} موظف
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      );
    }

    const section = sections.find((s) => s.id === selectedSectionId);

    return (
      <div className="bd-container" dir="rtl">
        <div className="bd-header">
          <button
            className="bd-back-btn"
            onClick={() => setSelectedSectionId(null)}
          >
            ← رجوع للأقسام
          </button>
          <div>
            <h2 className="bd-title">{section?.name}</h2>
            <p className="bd-subtitle">{dept?.name}</p>
          </div>
        </div>
        <div className="bd-search">
          <input
            type="text"
            placeholder="بحث بالاسم، الكود، أو المسمى الوظيفي..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bd-search-input"
          />
        </div>
        <EmployeeTable
          employees={section?.employees || []}
          searchTerm={searchTerm}
          onSelectHistory={setHistoryEmployee}
        />
      </div>
    );
  }

  if (isSectionViewer) {
    const dept = sectionCards[0];

    return (
      <div className="bd-container" dir="rtl">
        <div className="bd-header">
          <h2 className="bd-title">أقسام {dept?.name}</h2>
          <p className="bd-subtitle">
            إدارتك تعمل بأقسام داخل المقر — بدون فروع ميدانية
          </p>
        </div>
        <div className="bd-branches-grid">
          {dept?.sections?.length ? (
            dept.sections.map((item) => (
              <button
                key={item.id}
                className="bd-branch-card"
                onClick={() => {
                  setViewMode("department");
                  setSelectedId(dept.id);
                  setSelectedSectionId(item.id);
                }}
              >
                <div className="bd-branch-icon">📁</div>
                <div className="bd-branch-name">{item.name}</div>
                <div className="bd-branch-stats">
                  {item.employees.length} موظف
                </div>
              </button>
            ))
          ) : (
            <p className="bd-empty">لا يوجد موظفون في هذه الإدارة</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bd-container" dir="rtl">
      <div className="bd-header">
        <h2 className="bd-title">
          {isOrgWideViewer
            ? "الهيكل التنظيمي"
            : `${labels.sitePlural} والأقسام`}
        </h2>
        <p className="bd-subtitle">
          {isOrgWideViewer
            ? isFactory
              ? `كل الإدارات تظهر هنا — مع ${labels.sitePlural} وأقسام إنتاج لإدارة التشغيل`
              : `كل الإدارات تظهر هنا — مع ${labels.sitePlural} وأقسام مقر منفصلة لإدارة التشغيل`
            : showRegionalManagers
              ? `مديرو المناطق، ثم ${labels.sitePlural} الميدانية، ثم أقسام مقر التشغيل`
              : `${labels.sitePlural} الميدانية، ثم أقسام مقر التشغيل`}
        </p>
      </div>

      {isOrgWideViewer && (
        <>
          <div className="bd-section-header">
            <h3 className="bd-section-label">🏛️ كل الإدارات</h3>
            {canManageStructure && (
              <button
                type="button"
                className="bd-manage-btn"
                onClick={() => {
                  setShowDeptForm((v) => !v);
                  setDeptError("");
                }}
              >
                {showDeptForm ? "إلغاء" : "+ إضافة إدارة"}
              </button>
            )}
          </div>

          {canManageStructure && showDeptForm && (
            <div className="bd-org-form">
              <div className="bd-org-form-grid">
                <label>
                  اسم الإدارة
                  <input
                    type="text"
                    value={newDeptName}
                    onChange={(e) => {
                      setNewDeptName(e.target.value);
                      if (!newDeptCode.trim()) {
                        setNewDeptCode(suggestDepartmentCode(e.target.value));
                      }
                    }}
                    placeholder="مثال: المبيعات"
                  />
                </label>
                <label>
                  كود الإدارة
                  <input
                    type="text"
                    value={newDeptCode}
                    onChange={(e) =>
                      setNewDeptCode(e.target.value.toUpperCase())
                    }
                    placeholder="SALES"
                  />
                </label>
              </div>
              {deptError && <p className="bd-org-error">{deptError}</p>}
              <button
                type="button"
                className="bd-manage-btn is-primary"
                onClick={handleCreateDepartment}
                disabled={creatingDept}
              >
                {creatingDept ? "جاري الحفظ..." : "حفظ الإدارة"}
              </button>
            </div>
          )}

          <div className="bd-branches-grid">
            {sectionCards.length === 0 ? (
              <p className="bd-empty">لا توجد إدارات مسجلة</p>
            ) : (
              sectionCards.map((dept) => (
                <button
                  key={dept.id}
                  className="bd-branch-card bd-dept-card"
                  onClick={() => {
                    setViewMode("department");
                    setSelectedId(dept.id);
                    setSelectedSectionId(null);
                  }}
                >
                  <div className="bd-branch-icon">
                    {dept.isOps ? "🏢" : "🏛️"}
                  </div>
                  <div className="bd-branch-name">{dept.name}</div>
                  <div className="bd-branch-stats">
                    {dept.total} موظف
                    {!dept.isOps ? ` | ${dept.sections.length} قسم` : " | تشغيل"}
                    {dept.isActive === false ? " | غير مفعّلة" : ""}
                  </div>
                </button>
              ))
            )}
          </div>
        </>
      )}

      {isOpsViewer && (
        <>
          {showRegionalManagers ? (
            <>
              <h3 className="bd-section-label">مديري المناطق</h3>
              <p className="bd-subtitle" style={{ marginTop: 0 }}>
                يتبعون مدير {opsDeptName}، وكل مدير مسؤول عن مجموعة{" "}
                {labels.sitePlural}
              </p>
              <div className="bd-branches-grid">
                {regionalManagers.length === 0 ? (
                  <p className="bd-empty">لا يوجد مديرو مناطق</p>
                ) : (
                  regionalManagers.map((manager) => {
                    const branchNames = managedBranchNames(manager);
                    return (
                      <button
                        key={manager.id}
                        type="button"
                        className="bd-branch-card"
                        onClick={() => {
                          setViewMode("regional");
                          setSelectedId(manager.id);
                          setSearchTerm("");
                        }}
                      >
                        <div className="bd-branch-icon">🗺️</div>
                        <div className="bd-branch-name">{manager.name}</div>
                        <div className="bd-branch-stats">
                          {branchNames.length
                            ? branchNames.join("، ")
                            : `بدون ${labels.sitePlural} مرتبطة`}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </>
          ) : null}

          <div className="bd-section-header">
            <h3 className="bd-section-label">
              {isOrgWideViewer
                ? isFactory
                  ? `🏭 ${labels.sitePlural} الإنتاج`
                  : `🏢 ${labels.sitePlural} إدارة التشغيل`
                : `${labels.sitePlural} الميدانية`}
            </h3>
            {canManageStructure && (
              <button
                type="button"
                className="bd-manage-btn"
                onClick={() => {
                  setShowBranchForm((v) => !v);
                  setBranchError("");
                }}
              >
                {showBranchForm ? "إلغاء" : `+ إضافة ${labels.site}`}
              </button>
            )}
          </div>

          {canManageStructure && showBranchForm && (
            <div className="bd-org-form">
              <div className="bd-org-form-grid">
                <label>
                  اسم {labels.site}
                  <input
                    type="text"
                    value={newBranchName}
                    onChange={(e) => {
                      setNewBranchName(e.target.value);
                      if (!newBranchCode.trim()) {
                        setNewBranchCode(suggestBranchCode(e.target.value));
                      }
                    }}
                    placeholder={`مثال: ${labels.site} التجمع`}
                  />
                </label>
                <label>
                  كود {labels.site}
                  <input
                    type="text"
                    value={newBranchCode}
                    onChange={(e) =>
                      setNewBranchCode(e.target.value.toUpperCase())
                    }
                    placeholder="TAGAMOA"
                  />
                </label>
              </div>
              {branchError && <p className="bd-org-error">{branchError}</p>}
              <button
                type="button"
                className="bd-manage-btn is-primary"
                onClick={handleCreateBranch}
                disabled={creatingBranch}
              >
                {creatingBranch ? "جاري الحفظ..." : `حفظ ${labels.site}`}
              </button>
            </div>
          )}

          <div className="bd-branches-grid">
            {opsBranchCards.length === 0 ? (
              <p className="bd-empty">لا توجد {labels.sitePlural} بعد</p>
            ) : (
              opsBranchCards.map((branch) => (
                <button
                  key={branch.id}
                  className="bd-branch-card"
                  onClick={() => openBranch(branch.id)}
                >
                  <div className="bd-branch-icon">🏢</div>
                  <div className="bd-branch-name">{branch.name}</div>
                  <div className="bd-branch-stats">
                    {branch.total} موظف | {branch.active} نشط
                  </div>
                </button>
              ))
            )}
          </div>

          <h3 className="bd-section-label">
            📁 أقسام مقر {opsDeptName}
          </h3>
          <p className="bd-subtitle" style={{ marginTop: 0 }}>
            أقسام تابعة لمدير إدارة التشغيل
            {isFactory ? " (إنتاج / صيانة / جودة / مخازن)" : " (مثل المخزن)"} —
            بدون {labels.sitePlural} ميدانية
          </p>
          <div className="bd-branches-grid">
            {opsHqSections.length === 0 ? (
              <p className="bd-empty">
                لا توجد أقسام بعد — أضف مدير قسم من الموارد البشرية (مثل مدير
                قسم المخزن)
              </p>
            ) : (
              opsHqSections.map((section) => (
                <button
                  key={section.id}
                  className="bd-branch-card"
                  onClick={() => {
                    setViewMode("ops-hq");
                    setSelectedSectionId(section.id);
                  }}
                >
                  <div className="bd-branch-icon">📁</div>
                  <div className="bd-branch-name">{section.name}</div>
                  <div className="bd-branch-stats">
                    {section.employees.length} موظف
                  </div>
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default BranchesDepartments;
